import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  SignoffPhotos,
  SubmittedResponseReview,
  type SignoffAttachment,
  FormFillContextProvider,
  ValidationProvider,
  type EacoDocumentContent,
} from '@eaco/forms-ui-web';
import { formatDisplayDate } from '@eaco/forms-core';
import { FormsFileTransportProvider } from '@/lib/FormsFileTransportProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCircleCheck,
  faCircleXmark,
  faComment,
  faFlag,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { toast } from 'sonner';
import eacoMarkBlue from '@/assets/eaco-logo.svg';

import {
  FORM_SIGNOFF_REVIEW,
  SIGN_OFF_FORM_RESULT_EXTERNAL,
  type FormSignoffReview,
} from '@/queries/signoff';

const ACCENT = '#2B7AEA';
const GREEN = '#1a8a5c';
const RED = '#c0303c';
const AMBER = '#c2730c';

type Verdict = 'satisfactory' | 'unsatisfactory';

const initialsFor = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts
    .slice(0, 2)
    .map((p) => p[0])
    .join('');
  return (letters || '?').toUpperCase();
};

// The platform shape, so a reviewer on the emailed link reads the same
// date the staff app and the portal show them.
const formatDate = (iso: string | null | undefined): string =>
  formatDisplayDate(iso, { fallback: '' });

const SignOffView: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const { data, loading, error } = useQuery<{
    formSignoffReview: FormSignoffReview;
  }>(FORM_SIGNOFF_REVIEW, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });

  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [overall, setOverall] = useState('');
  const [signerName, setSignerName] = useState('');
  const [done, setDone] = useState<Verdict | null>(null);
  // Per-answer comments, controlled by element id.
  const [elementComments, setElementComments] = useState<
    Record<string, string>
  >({});
  const onCommentChange = useCallback((elementId: string, text: string) => {
    setElementComments((prev) => ({ ...prev, [elementId]: text }));
  }, []);
  // Per-answer photos, and the reviewer's photos of the response as a whole.
  const [elementAttachments, setElementAttachments] = useState<
    Record<string, SignoffAttachment[]>
  >({});
  const [overallAttachments, setOverallAttachments] = useState<
    SignoffAttachment[]
  >([]);
  const onAttachmentsChange = useCallback(
    (elementId: string, next: SignoffAttachment[]) => {
      setElementAttachments((prev) => ({ ...prev, [elementId]: next }));
    },
    [],
  );
  const commentCount = useMemo(
    () => Object.values(elementComments).filter((s) => s.trim()).length,
    [elementComments],
  );

  const [signOff, { loading: submitting }] = useMutation(
    SIGN_OFF_FORM_RESULT_EXTERNAL,
  );

  const review = data?.formSignoffReview;
  const result = review?.result ?? null;
  const ownerName = review?.ownerName || 'the sender';

  const content = useMemo(
    () => (result?.versionContent as EacoDocumentContent | null) ?? null,
    [result?.versionContent],
  );
  const values = useMemo(
    () => (result?.responses as Record<string, unknown>) ?? {},
    [result?.responses],
  );

  if (loading) {
    return <CenterShell>Loading…</CenterShell>;
  }

  if (error || !review || !result) {
    return (
      <TerminalCard
        ownerName={review?.ownerName ?? null}
        status="Link expired"
        icon={faTriangleExclamation}
        tone={RED}
        title="This sign-off link is invalid or has expired"
        subtitle="Ask the sender to re-share the response. You can close this window."
      />
    );
  }

  // Display-only labels + comment permissions from the form's sign-off
  // config (verdict values sent to the server are unchanged).
  const acceptLabel = review.acceptLabel || 'Satisfactory';
  const declineLabel = review.declineLabel || 'Unsatisfactory';
  const canComment = review.canComment;
  const canOverallComment = review.canOverallComment;
  // Acknowledgement-only: the reviewer can only confirm — no decline option.
  const acknowledgementOnly = review.acknowledgementOnly;
  const reviewerTitle = (review.reviewerTitle ?? '').trim();

  if (done) {
    return (
      <TerminalCard
        ownerName={review.ownerName ?? null}
        status={done === 'satisfactory' ? acceptLabel : declineLabel}
        icon={done === 'satisfactory' ? faCircleCheck : faCircleXmark}
        tone={done === 'satisfactory' ? GREEN : RED}
        title={done === 'satisfactory' ? acceptLabel : declineLabel}
        subtitle="Thanks — your decision has been recorded. You can close this window."
      />
    );
  }

  if (review.alreadyCompleted) {
    return (
      <TerminalCard
        ownerName={review.ownerName ?? null}
        status="Completed"
        icon={faCircleCheck}
        tone={ACCENT}
        title="Already signed off"
        subtitle={`This response for ${result.formName || 'the form'} has already been signed off. No further action is needed.`}
        details={
          review.reviewerName
            ? [{ label: 'Reviewer', value: review.reviewerName }]
            : undefined
        }
      />
    );
  }

  const submitter = result.submittedBy?.name || result.fillerEmail || null;
  const scorePercent =
    result.scorePercent != null ? Math.round(result.scorePercent) : null;
  const flagged = result.flaggedAnswerCount ?? 0;

  // Known reviewers (a resolved contact) come with a name from the server — we
  // don't ask them for it. A raw-email reviewer has no known name, so they must
  // type one. `effectiveSignerName` is what we record + show either way.
  const knownName = (review.reviewerName ?? '').trim();
  const nameKnown = knownName !== '';
  const effectiveSignerName = nameKnown ? knownName : signerName;

  const rejecting = verdict === 'unsatisfactory';
  // A comment is only required — and only offered — when the form allows an
  // overall reviewer comment. When it does, the negative (decline) decision
  // must carry one. The server enforces the authoritative rule.
  const overallCommentRequired = rejecting && canOverallComment;
  const canSubmit =
    !!verdict &&
    (nameKnown || signerName.trim() !== '') &&
    (!overallCommentRequired || overall.trim() !== '') &&
    !submitting;

  const submit = async () => {
    if (!verdict) return;
    const trimmedComments: Record<string, string> = {};
    for (const [k, v] of Object.entries(elementComments)) {
      if (v.trim()) trimmedComments[k] = v.trim();
    }
    const photos: Record<string, SignoffAttachment[]> = {};
    for (const [k, v] of Object.entries(elementAttachments)) {
      if (v.length) photos[k] = v;
    }
    try {
      const res = await signOff({
        variables: {
          input: {
            token,
            // FormSignoffVerdict is an uppercase enum.
            verdict:
              verdict === 'satisfactory' ? 'SATISFACTORY' : 'UNSATISFACTORY',
            overallComment: overall.trim() || null,
            elementComments: trimmedComments,
            overallAttachments,
            elementAttachments: photos,
            reviewerName: effectiveSignerName.trim() || null,
          },
        },
      });
      const payload = res.data?.signOffFormResultExternal;
      if (payload?.success) {
        setDone(verdict);
      } else {
        toast.error(payload?.message || 'Could not record your sign-off');
      }
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Could not record your sign-off',
      );
    }
  };

  const ringCirc = 2 * Math.PI * 26;
  const ringDash =
    scorePercent != null
      ? `${((scorePercent / 100) * ringCirc).toFixed(1)} ${ringCirc.toFixed(1)}`
      : `0 ${ringCirc.toFixed(1)}`;

  return (
    <div className="flex min-h-screen flex-col bg-[#e8edf2]">
      <div className="mx-auto w-full max-w-[680px] flex-1 px-4 py-8">
        <div className="overflow-hidden rounded-[16px] border border-[#e6e9ee] bg-white shadow-[0_18px_50px_-28px_rgba(16,24,40,.35)]">
          {/* Co-brand header */}
          <div
            className="flex items-center justify-between gap-3 px-[26px] py-[18px]"
            style={{ borderBottom: `2px solid ${ACCENT}` }}
          >
            <span className="flex min-w-0 items-center gap-[11px]">
              <span
                className="flex h-[40px] w-[40px] flex-none items-center justify-center rounded-[10px] text-[14px] font-extrabold text-white"
                style={{ backgroundColor: ACCENT }}
              >
                {initialsFor(review.ownerName || 'Eaco')}
              </span>
              <span className="truncate text-[16px] font-extrabold tracking-[-0.01em] text-[#1f2a3d]">
                {review.ownerName || 'Sign-off'}
              </span>
            </span>
            <span
              className="flex-none rounded-[20px] px-[10px] py-[4px] text-[10px] font-extrabold uppercase tracking-[0.08em]"
              style={{ color: ACCENT, backgroundColor: '#eef4fd' }}
            >
              Sign-off request
            </span>
          </div>

          <div className="px-[26px] py-[26px] sm:px-[40px]">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-[#8a93a3]">
              {result.formName || 'Response'}
            </div>
            <h1 className="mt-[6px] text-[26px] font-extrabold leading-[1.2] tracking-[-0.02em] text-[#1f2a3d]">
              Please review &amp; sign off
            </h1>
            <p className="mt-[10px] text-[13.5px] leading-[1.6] text-[#4a5262]">
              {submitter ? (
                <>
                  Submitted by{' '}
                  <b className="text-[#1f2a3d]">{submitter}</b>
                  {result.submittedAt
                    ? ` · ${formatDate(result.submittedAt)}`
                    : ''}
                  .{' '}
                </>
              ) : (
                ''
              )}
              Read each answer below, add comments where you need to, then sign
              off at the bottom.
            </p>

            {/* Score summary (only when the form reveals it) */}
            {scorePercent != null && (
              <div className="mt-[18px] flex items-center gap-[16px] rounded-[14px] border border-[#e6ebf1] bg-[#f4f7fa] px-[17px] py-[15px]">
                <div className="relative h-[58px] w-[58px] flex-none">
                  <svg width="58" height="58" viewBox="0 0 64 64">
                    <circle
                      cx="32"
                      cy="32"
                      r="26"
                      fill="none"
                      stroke="#e2e8ef"
                      strokeWidth="7"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="26"
                      fill="none"
                      stroke={GREEN}
                      strokeWidth="7"
                      strokeLinecap="round"
                      strokeDasharray={ringDash}
                      transform="rotate(-90 32 32)"
                    />
                  </svg>
                  <div
                    className="absolute inset-0 flex items-center justify-center text-[14px] font-extrabold"
                    style={{ color: GREEN }}
                  >
                    {scorePercent}%
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-extrabold" style={{ color: GREEN }}>
                    Automated score
                  </div>
                  <div className="mt-[6px] flex flex-wrap items-center gap-[13px]">
                    {result.score != null && result.scoreMax != null && (
                      <span className="text-[12px] font-bold text-[#6a7383]">
                        {result.score} / {result.scoreMax}
                      </span>
                    )}
                    {flagged > 0 && (
                      <span
                        className="inline-flex items-center gap-[6px] text-[12px] font-bold"
                        style={{ color: RED }}
                      >
                        <FontAwesomeIcon icon={faFlag} style={{ fontSize: 11 }} />
                        {flagged} flagged
                      </span>
                    )}
                    {commentCount > 0 && (
                      <span
                        className="inline-flex items-center gap-[6px] text-[12px] font-bold"
                        style={{ color: ACCENT }}
                      >
                        <FontAwesomeIcon
                          icon={faComment}
                          style={{ fontSize: 11 }}
                        />
                        {commentCount} comment{commentCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* If score is hidden but there are flagged answers the form still
                reveals, surface that alone. */}
            {scorePercent == null && flagged > 0 && (
              <div
                className="mt-[16px] inline-flex items-center gap-[7px] rounded-[9px] border px-[12px] py-[7px] text-[12px] font-bold"
                style={{
                  color: '#a06010',
                  background: '#fdf6ea',
                  borderColor: '#f4e2c4',
                }}
              >
                <FontAwesomeIcon icon={faFlag} style={{ fontSize: 12 }} />
                {flagged} answer(s) flagged for attention
              </div>
            )}

            {/* Submitted response — compact answer list with per-answer
                reviewer comments (design "Submitted response"). */}
            <div className="mt-[22px]">
              {content ? (
                <FormFillContextProvider
                  formOid={result.formDocumentId ?? null}
                  publicSlug={null}
                  formDocumentResultId={result.id ?? null}
                  secretToken={null}
                >
                  <ValidationProvider
                    invalidIds={new Set<string>()}
                    focusElementId={null}
                    focusToken={0}
                    pageJumps={new Map()}
                  >
                    <FormsFileTransportProvider signoffToken={token ?? null}>
                      <SubmittedResponseReview
                        content={content}
                        responses={values}
                        readOnly={!canComment}
                        comments={canComment ? elementComments : undefined}
                        attachments={
                          canComment ? elementAttachments : undefined
                        }
                        onCommentChange={
                          canComment ? onCommentChange : undefined
                        }
                        onAttachmentsChange={
                          canComment ? onAttachmentsChange : undefined
                        }
                        commenterName={effectiveSignerName.trim() || 'You'}
                        commenterInitials={
                          effectiveSignerName.trim()
                            ? initialsFor(effectiveSignerName)
                            : 'You'
                        }
                      />
                    </FormsFileTransportProvider>
                  </ValidationProvider>
                </FormFillContextProvider>
              ) : (
                <div className="rounded-[12px] border border-[#eef1f4] p-6 text-[13px] text-[#6a7383]">
                  The response preview is unavailable, but you can still record
                  your sign-off below.
                </div>
              )}
            </div>

            {/* Sign-off card */}
            <div className="mt-[22px] rounded-[15px] border-[1.5px] border-[#e0e5ec] bg-white p-[18px] shadow-[0_10px_30px_-20px_rgba(16,24,40,.25)]">
              <div className="text-[16px] font-extrabold tracking-[-0.01em] text-[#1f2a3d]">
                Your sign-off
              </div>
              <div className="mt-[4px] text-[12.5px] leading-[1.5] text-[#6a7383]">
                Confirm you've reviewed this response. Your name and the time are
                recorded on the record.
              </div>

              <div
                className={`mt-[16px] grid gap-[10px] ${
                  acknowledgementOnly ? 'grid-cols-1' : 'grid-cols-2'
                }`}
              >
                <VerdictButton
                  active={verdict === 'satisfactory'}
                  onClick={() => setVerdict('satisfactory')}
                  icon={faCircleCheck}
                  label={acceptLabel}
                  tone={GREEN}
                />
                {!acknowledgementOnly && (
                  <VerdictButton
                    active={verdict === 'unsatisfactory'}
                    onClick={() => setVerdict('unsatisfactory')}
                    icon={faCircleXmark}
                    label={declineLabel}
                    tone={RED}
                  />
                )}
              </div>

              {canOverallComment && (
                <>
                  <div className="mt-[14px] text-[11px] font-extrabold uppercase tracking-[0.05em] text-[#9aa3b0]">
                    Overall comment
                    {overallCommentRequired && (
                      <span style={{ color: AMBER }}>
                        {' '}
                        · required for {declineLabel.toLowerCase()}
                      </span>
                    )}
                  </div>
                  <textarea
                    value={overall}
                    onChange={(e) => setOverall(e.target.value)}
                    rows={3}
                    placeholder={
                      overallCommentRequired
                        ? 'Explain what needs to change…'
                        : 'Add a note for the record (optional)…'
                    }
                    className="mt-[6px] w-full resize-y rounded-[10px] border border-[#e3e7ec] bg-[#fbfcfd] p-[10px] text-[13px] text-[#1f2a3d] outline-none focus:border-[#2B7AEA]"
                  />
                  {/* Photos of the response as a whole. Its own providers:
                      this block sits outside the answer list, and the
                      uploader needs the tokenised transport. */}
                  <FormFillContextProvider
                    formOid={result.formDocumentId ?? null}
                    publicSlug={null}
                    formDocumentResultId={result.id ?? null}
                    secretToken={null}
                  >
                    <FormsFileTransportProvider signoffToken={token ?? null}>
                      <SignoffPhotos
                        photos={overallAttachments}
                        readOnly={false}
                        onChange={setOverallAttachments}
                        addLabel="Add a photo"
                      />
                    </FormsFileTransportProvider>
                  </FormFillContextProvider>
                </>
              )}

              {!nameKnown && (
                <>
                  <div className="mt-[14px] text-[11px] font-extrabold uppercase tracking-[0.05em] text-[#9aa3b0]">
                    Type your full name to sign
                  </div>
                  <input
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="e.g. Priya Tan"
                    className="mt-[6px] w-full rounded-[10px] border border-[#e3e7ec] bg-[#fbfcfd] px-[13px] py-[11px] text-[14px] text-[#1f2a3d] outline-none focus:border-[#2B7AEA]"
                  />
                </>
              )}
              <div className="mt-[9px] flex items-center gap-[6px] text-[11.5px] text-[#9aa3b0]">
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: 11 }} />
                <span>
                  {nameKnown ? (
                    <>
                      Signing as <b className="text-[#6a7383]">{knownName}</b>
                      {reviewerTitle ? (
                        <span className="text-[#6a7383]">, {reviewerTitle}</span>
                      ) : null}{' '}
                      · timestamped automatically.
                    </>
                  ) : (
                    <>
                      Your signature is timestamped automatically. This secure
                      link was issued to you.
                    </>
                  )}
                </span>
              </div>

              <button
                type="button"
                disabled={!canSubmit}
                onClick={submit}
                className="mt-[15px] inline-flex w-full items-center justify-center gap-[8px] rounded-[10px] px-[18px] py-[12px] text-[13.5px] font-extrabold text-white shadow-[0_2px_8px_rgba(26,138,92,.3)] disabled:opacity-50"
                style={{
                  backgroundColor: rejecting ? RED : GREEN,
                }}
              >
                <FontAwesomeIcon
                  icon={rejecting ? faCircleXmark : faCheck}
                  style={{ fontSize: 15 }}
                />
                {submitting
                  ? 'Submitting…'
                  : rejecting
                  ? `${declineLabel} & sign off`
                  : `${acceptLabel} & sign off`}
              </button>
            </div>
          </div>

          <div className="border-t border-[#eef1f4] bg-[#fbfcfd] px-[26px] py-[18px]">
            <div className="mb-[8px] flex items-center gap-[7px]">
              <span className="text-[11px] text-[#aab2bd]">Powered by</span>
              <img src={eacoMarkBlue} alt="Eaco" className="h-[15px] w-auto" />
            </div>
            <div className="text-[11.5px] leading-[1.6] text-[#9aa3b0]">
              Sent by <b className="text-[#6a7383]">eaco</b> on behalf of{' '}
              <b className="text-[#6a7383]">{ownerName}</b>. This secure link was
              issued to you.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VerdictButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: typeof faCircleCheck;
  label: string;
  tone: string;
}> = ({ active, onClick, icon, label, tone }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center justify-center gap-[8px] rounded-[10px] border-[1.5px] px-[12px] py-[12px] text-[13px] font-extrabold transition-colors"
    style={{
      borderColor: active ? tone : '#e2e6ec',
      backgroundColor: active ? `${tone}12` : '#fff',
      color: active ? tone : '#4a5262',
    }}
  >
    <FontAwesomeIcon icon={icon} style={{ fontSize: 15 }} />
    {label}
  </button>
);

const CenterShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#e8edf2] p-6 text-[13px] text-[#6a7383]">
    {children}
  </div>
);

/** Co-branded terminal card — the approved / rejected / expired / already-done states. */
const TerminalCard: React.FC<{
  ownerName: string | null;
  status: string;
  icon: typeof faCircleCheck;
  tone: string;
  title: string;
  subtitle: string;
  details?: { label: string; value: string }[];
}> = ({ ownerName, status, icon, tone, title, subtitle, details }) => (
  <div className="flex min-h-screen flex-col bg-[#e8edf2]">
    <div className="mx-auto flex w-full max-w-[456px] flex-1 items-center px-4 py-8">
      <div className="w-full overflow-hidden rounded-[16px] border border-[#e6e9ee] bg-white shadow-[0_18px_50px_-30px_rgba(16,24,40,.35)]">
        <div
          className="flex items-center justify-between gap-3 px-[22px] py-[14px]"
          style={{ borderBottom: `2px solid ${tone}` }}
        >
          <span className="flex min-w-0 items-center gap-[10px]">
            <span
              className="flex h-[36px] w-[36px] flex-none items-center justify-center rounded-[9px] text-[13px] font-extrabold text-white"
              style={{ backgroundColor: tone }}
            >
              {initialsFor(ownerName || 'Eaco')}
            </span>
            <span className="truncate text-[15px] font-extrabold tracking-[-0.01em] text-[#1f2a3d]">
              {ownerName || 'Sign-off'}
            </span>
          </span>
          <span
            className="flex-none rounded-[20px] px-[10px] py-[4px] text-[10px] font-extrabold uppercase tracking-[0.07em]"
            style={{ color: tone, backgroundColor: `${tone}14` }}
          >
            {status}
          </span>
        </div>
        <div className="px-[34px] py-[34px] text-center">
          <div
            className="mx-auto flex h-[66px] w-[66px] items-center justify-center rounded-full"
            style={{ backgroundColor: `${tone}1a` }}
          >
            <FontAwesomeIcon
              icon={icon}
              style={{ fontSize: 31, color: tone }}
            />
          </div>
          <h1 className="mt-[18px] text-[22px] font-extrabold tracking-[-0.02em] text-[#1f2a3d]">
            {title}
          </h1>
          <div className="mt-[8px] text-[13.5px] leading-[1.6] text-[#4a5262]">
            {subtitle}
          </div>
          {details && details.length > 0 && (
            <div className="mt-[20px] flex flex-col gap-[9px] rounded-[12px] border border-[#eef1f4] bg-[#fbfcfd] px-[17px] py-[15px] text-left">
              {details.map((d, i) => (
                <div
                  key={d.label}
                  className={
                    i > 0 ? 'border-t border-[#eef1f4] pt-[9px]' : undefined
                  }
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[12px] font-bold text-[#8a93a3]">
                      {d.label}
                    </span>
                    <span className="text-[12.5px] font-bold text-[#1f2a3d]">
                      {d.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-[16px] text-[12px] text-[#9aa3b0]">
            You can safely close this window.
          </div>
        </div>
        <div className="border-t border-[#eef1f4] bg-[#fbfcfd] px-[22px] py-[15px]">
          <div className="flex items-center justify-center gap-[7px]">
            <span className="text-[11px] text-[#aab2bd]">Powered by</span>
            <img src={eacoMarkBlue} alt="Eaco" className="h-[14px] w-auto" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default SignOffView;
